/**
 * "Is this title an occasion, and whose?" — shared because two features need
 * the same answer and must not drift apart.
 *
 * Lifted verbatim out of `dispatch-notifications/giftPromptCandidates.ts`
 * (#1666), which now re-exports from here. #1670's amendment calls for exactly
 * this move: create_event in kin-chat has to classify a birthday title too,
 * and an Edge Function cannot import from a sibling function's directory —
 * `_shared/` is the only place both can reach, and a change here redeploys
 * every consumer, which a sibling-directory import would not.
 *
 * ⚠️ Behaviour is unchanged on purpose. The gift heads-up decides whether to
 * ask about a present off these two functions, so a "tidy-up" to the regexes
 * silently changes which events prompt.
 */

export type OccasionKind = "birthday" | "anniversary" | "party" | "shower" | "wedding";

const KIND_RULES: Array<[OccasionKind, RegExp]> = [
  ["birthday", /\b(birthday|bday|b-day)\b/i],
  ["anniversary", /\banniversary\b/i],
  ["shower", /\b(baby|bridal|wedding)?\s*shower\b/i],
  ["wedding", /\bwedding\b/i],
  ["party", /\bparty\b/i],
];

/** "Carson's birthday party" → birthday (first rule wins, and birthday is
 *  listed before party on purpose). Anything else → null. */
export function classifyGiftTitle(title: string | null | undefined): OccasionKind | null {
  const t = String(title ?? "");
  if (!t.trim()) return null;
  for (const [kind, re] of KIND_RULES) if (re.test(t)) return kind;
  return null;
}

// Case-insensitive on the first letter on purpose: a calendar title typed as
// "carson's bday" is the same person as "Carson's birthday".
const POSSESSIVE = /([A-Za-z][\w.-]*(?:\s+[A-Z][\w.-]*)?)['’]s\b/;
const FOR_NAME = /\bfor\s+([A-Z][\w.-]*(?:\s+[A-Z][\w.-]*)?)/;
const KIND_WORDS = /\b(birthday|bday|b-day|anniversary|baby|bridal|shower|wedding|party)\b/gi;

/** Whose event this is: the possessive ("Carson's …"), else "for X", else the
 *  title with the kind words stripped, else the title itself. */
export function personFromTitle(title: string): string {
  const t = String(title ?? "").trim();
  const poss = POSSESSIVE.exec(t);
  if (poss) return poss[1];
  const forName = FOR_NAME.exec(t);
  if (forName) return forName[1];
  const stripped = t.replace(KIND_WORDS, "").replace(/[\s\-–—:]+/g, " ").trim();
  return stripped || t;
}

export function normPerson(name: string): string {
  return String(name ?? "")
    .toLowerCase()
    .replace(/['’]s\b/g, "") // a stray possessive is not part of the name
    .replace(/[’']/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Is this title a BIRTHDAY specifically, and is it about a party rather than
 * the day itself?
 *
 * #1670's amendment turns on this distinction. "Carly's birthday" should
 * teach Kin a recurring fact; "Carly's birthday party" is a real one-off
 * event that happens to mention a birthday, and forcing a durable birthdate
 * out of it would record the party's date as her birthday — which is often a
 * nearby Saturday, not the day she was born.
 */
export function isBirthdayItself(title: string | null | undefined): boolean {
  if (classifyGiftTitle(title) !== "birthday") return false;
  return !/\bparty\b/i.test(String(title ?? ""));
}
